class CartDrawerSection extends HTMLElement {
  cartUpdateUnsubscriber = undefined;

  constructor() {
    super();

    this.cartType = this.dataset.cartType;
    this.drawerClass = "wt-cart__drawer";
    this.drawer = this.querySelector(`.${this.drawerClass}`);
    this.classDrawerActive = `${this.drawerClass}--open`;
    this.pageOverlayClass = "page-overlay-cart";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;
    this.triggerQuery = [
      `.wt-cart__trigger`,
      `[data-rd-cart-trigger]`,
      `.wt-cart__back-link`,
      `.${this.pageOverlayClass}`,
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);
    this.isOpen = false;
    this.isCartPage = window.location.pathname === window.routes.cart_url;
    this.closeButton = () => this.querySelector(".wt-cart__drawer__close");
    this.mainTrigger = document.querySelector(".wt-cart__trigger");
    this.toggleEelements = () =>
      this.querySelectorAll(this.dataset.toggleTabindex);

    this.openerEl = null;
    this.boundHandleTriggerClick = this.handleTriggerClick.bind(this);
    this.boundHandleCustomOpen = this.open.bind(this);
    this.boundHandleCustomClose = this.close.bind(this);
  }

  connectedCallback() {
    if (this.cartType === "page" || this.isCartPage) {
      document.addEventListener("cart-drawer:refresh", (e) =>
        this.refreshCartDrawer(e),
      );
      return;
    }

    this.init();
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      this.prepareRecommendedProducts();
      this.refreshCartCountFromApi();

      if (this.isOpen) {
        setTabindex(this.toggleEelements(), "0");
        this.closeButton()?.focus();
      }
    });
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.boundHandleTriggerClick, true);
    document.removeEventListener("cart:open", this.boundHandleCustomOpen);
    document.removeEventListener("cart-drawer:open", this.boundHandleCustomOpen);
    document.removeEventListener("theme:cart:open", this.boundHandleCustomOpen);
    document.removeEventListener("drawer:open", this.boundHandleCustomOpen);
    document.removeEventListener("raj:cart:open", this.boundHandleCustomOpen);
    document.removeEventListener("cart:close", this.boundHandleCustomClose);
    document.removeEventListener("cart-drawer:close", this.boundHandleCustomClose);

    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  rememberOpener(e) {
    const current = e?.currentTarget || e?.target;
    const active = document.activeElement;

    const interactive =
      current instanceof HTMLElement
        ? current.closest?.("button, a, [tabindex], input, select") || current
        : null;

    this.openerEl =
      interactive instanceof HTMLElement
        ? interactive
        : active instanceof HTMLElement
          ? active
          : null;
  }

  restoreFocusToOpener() {
    const el = this.openerEl;
    if (!el) return;
    if (!document.contains(el)) return;

    const isDisabled =
      el.hasAttribute("disabled") ||
      el.getAttribute("aria-disabled") === "true";
    if (isDisabled) return;

    requestAnimationFrame(() => {
      el.focus?.();
    });
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input, select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  temporaryHideFocusVisible() {
    document.body.classList.add("no-focus-visible");
  }

  setDrawerState(shouldOpen) {
    if (!this.drawer) {
      this.drawer = this.querySelector(`.${this.drawerClass}`);
    }

    if (!this.drawer) return;
    if (shouldOpen === this.isOpen) return;

    if (shouldOpen) {
      this.prepareRecommendedProducts();
      this.refreshCartCountFromApi();
      this.setAttribute("open", "");
      this.setAttribute("aria-hidden", "false");
      this.isOpen = true;
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "0");

      requestAnimationFrame(() => {
        this.closeButton()?.focus();
      });
    } else {
      this.removeAttribute("open");
      this.setAttribute("aria-hidden", "true");
      this.isOpen = false;
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "-1");
      this.restoreFocusToOpener();
    }

    this.drawer.classList.toggle(this.classDrawerActive, shouldOpen);
    this.body.classList.toggle(this.activeOverlayBodyClass, shouldOpen);

    const eventName = shouldOpen
      ? PUB_SUB_EVENTS.cartDrawerOpen
      : PUB_SUB_EVENTS.cartDrawerClose;

    document.dispatchEvent(
      new CustomEvent(eventName, {
        bubbles: true,
      }),
    );
  }

  onToggle() {
    this.setDrawerState(!this.isOpen);
  }

  toggleDrawerClasses(forceOpen) {
    if (typeof forceOpen === "boolean") {
      this.setDrawerState(forceOpen);
      return;
    }

    this.setDrawerState(!this.isOpen);
  }

  open(eventOrElement) {
    if (eventOrElement instanceof Event) {
      this.rememberOpener(eventOrElement);
    } else if (eventOrElement instanceof HTMLElement) {
      this.openerEl = eventOrElement;
    }

    this.setDrawerState(true);
  }

  close() {
    this.setDrawerState(false);
  }

  handleTriggerClick(e) {
    const trigger = e.target?.closest?.(this.triggerQuery);
    if (!trigger || this.isCartPage) return;

    e.preventDefault();
    e.stopPropagation();

    if (!this.isOpen) {
      this.rememberOpener({ currentTarget: trigger, target: trigger });
    }

    this.toggleDrawerClasses();
  }

  init() {
    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) {
          this.close();
        }
      }

      if (isTabPressed) {
        if (e.shiftKey && document.activeElement === first) {
          last?.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first?.focus();
          e.preventDefault();
        }
      }
    });

    document.removeEventListener("click", this.boundHandleTriggerClick, true);
    document.addEventListener("click", this.boundHandleTriggerClick, true);

    this.addEventListener("click", (e) => {
      if (e.target.classList.contains("wt-cart__drawer__close")) {
        e.preventDefault();
        this.close();
      }
    });

    document.addEventListener("cart-drawer:refresh", (e) =>
      this.refreshCartDrawer(e),
    );

    document.addEventListener("cart:open", this.boundHandleCustomOpen);
    document.addEventListener("cart-drawer:open", this.boundHandleCustomOpen);
    document.addEventListener("theme:cart:open", this.boundHandleCustomOpen);
    document.addEventListener("drawer:open", this.boundHandleCustomOpen);
    document.addEventListener("raj:cart:open", this.boundHandleCustomOpen);
    document.addEventListener("cart:close", this.boundHandleCustomClose);
    document.addEventListener("cart-drawer:close", this.boundHandleCustomClose);

    this.prepareRecommendedProducts();
    this.refreshCartCountFromApi();
  }

  prepareRecommendedProducts() {
    const grids = this.querySelectorAll('[data-rd-cart-reco-grid]');
    if (!grids.length) return;

    grids.forEach((grid) => {
      const items = Array.from(grid.querySelectorAll('[data-rd-reco-item]'));
      if (!items.length) return;

      const limit = Math.max(
        1,
        parseInt(grid.getAttribute('data-rd-reco-limit') || '4', 10) || 4,
      );

      const shuffled = items
        .map((item) => ({ item, sort: Math.random() }))
        .sort((a, b) => a.sort - b.sort)
        .map(({ item }) => item);

      shuffled.forEach((item, index) => {
        item.style.display = index < limit ? '' : 'none';
        grid.appendChild(item);
      });
    });
  }

  updateCountBubblesFromCart(cart) {
    if (!cart) return;

    const lineCount = Array.isArray(cart.items)
      ? cart.items.length
      : parseInt(cart.item_count || 0, 10) || 0;
    const displayCount = lineCount > 99 ? '99+' : String(lineCount);

    document
      .querySelectorAll('[data-rd-cart-count], #cart-icon-bubble .wt-header__panel__counter')
      .forEach((bubble) => {
        bubble.textContent = displayCount;
        bubble.setAttribute('aria-label', lineCount + ' products in cart');

        if (lineCount > 0) {
          bubble.removeAttribute('hidden');
        } else {
          bubble.setAttribute('hidden', 'hidden');
        }
      });

    document.dispatchEvent(
      new CustomEvent('raj:cart:updated', {
        bubbles: true,
        detail: {
          cart,
          line_count: lineCount,
        },
      }),
    );
  }

  refreshCartCountFromApi() {
    const cartJsonUrl = window.routes?.cart_url
      ? `${window.routes.cart_url}.js`
      : `${window.Shopify?.routes?.root || '/'}cart.js`;

    fetch(cartJsonUrl + (cartJsonUrl.includes('?') ? '&' : '?') + '_=' + Date.now(), {
      method: 'GET',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'Cache-Control': 'no-cache',
      },
    })
      .then((response) => {
        if (!response.ok) throw response;
        return response.json();
      })
      .then((cart) => this.updateCountBubblesFromCart(cart))
      .catch(() => {});
  }

  renderContents(parsedState, isClosedCart = true) {
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement || !parsedState.sections?.[section.id]) return;

      const nextHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector,
      );

      if (nextHTML !== null) {
        sectionElement.innerHTML = nextHTML;
      }
    });

    this.drawer = this.querySelector(`.${this.drawerClass}`);
    this.prepareRecommendedProducts();
    this.refreshCartCountFromApi();

    if (isClosedCart) {
      setTimeout(() => {
        this.open();
      });
    }
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    const parsed = new DOMParser().parseFromString(html, "text/html");
    const element = parsed.querySelector(selector);
    return element ? element.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  refreshCartDrawer(e) {
    const sectionsToRender = this.getSectionsToRender();
    fetch(
      `${window.Shopify.routes.root}?sections=${sectionsToRender[0].id},${sectionsToRender[1].id}`,
    )
      .then((response) => response.json())
      .then((response) => {
        const parsedState = {
          sections: response,
        };
        this.renderContents(parsedState, false);
      })
      .catch((e) => {
        console.log(e);
      });
  }

  setActiveElement(element) {
    this.activeElement = element;
    if (element instanceof HTMLElement) {
      this.openerEl = element;
    }
  }
}

if (!customElements.get("cart-drawer")) {
  customElements.define("cart-drawer", CartDrawerSection);
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

if (!customElements.get("cart-drawer-items")) {
  customElements.define("cart-drawer-items", CartDrawerItems);
}
